import { Button, Flex, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { v4 as uuidv4 } from 'uuid'
import { useContext, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { IconFilePlus, IconTrash } from '@tabler/icons-react'
import { HubSpeciTable } from '@/components/speci/hub-speci-table/hub-speci-table'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { ListSpeciGql } from '@/graphql/queries/speci/list-speci.graphql'
import { DeleteSpeciGql } from '@/graphql/mutations/speci/speci-delete.graphql'
import { showSuccessNotification } from '@shared/helpers/notifications/notifications'
import { LoomQuery, SpeciType } from '@loom-api-types'
import { StationContext } from '@/pages/stations/station-context'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { EmailSpeciModal } from '@/components/speci/email-speci-modal/email-speci-modal'
import { useNavigation } from '@/hooks/use-navigation'

type SpeciToDelete = {
  id: string
  author?: string | null
  created: string
} | null

type SpeciToResend = {
  id: string
} | null

export function StationSpeciPage() {
  const station = useContext(StationContext)
  const queryClient = useQueryClient()
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false)
  const [isResendModalOpen, { open: openResendModal, close: closeResendModal }] =
    useDisclosure(false)
  const [speciToDelete, setSpeciToDelete] = useState<SpeciToDelete>(null)
  const [speciToResend, setSpeciToResend] = useState<SpeciToResend>(null)
  const { navigateToNewSpeci } = useNavigation()

  const queryKey = useMemo(() => {
    return ['listSpecis', station?.recordNumber]
  }, [station?.recordNumber])

  const { data, isError, isLoading } = useAuthenticatedGraphQuery({
    queryKey,
    gql: ListSpeciGql,
    queryParams: {
      stationRecordNumber: station?.recordNumber,
      speciType: SpeciType.Hub,
    },
  })

  // Mutation to delete the speci
  const deleteSpeciMutation = useAuthenticatedGraphMutation({
    gql: DeleteSpeciGql,
    gqlKey: 'deleteSpeci',
    onSuccess: () => {
      showSuccessNotification('Wool Specification deleted')
    },
  })

  const askForDeleteConfirmation = (speciId: string) => {
    const speci = data?.listSpecis?.results?.find((speci) => speci.id === speciId)
    setSpeciToDelete(
      speci
        ? {
            id: speciId,
            author: speci.originalSpeciSubmittedBy,
            created: speci.originalSpeciCreated,
          }
        : null
    )
    openDeleteModal()
  }

  const openResendEmailModal = (speciId: string) => {
    setSpeciToResend({ id: speciId })
    openResendModal()
  }

  const cancelDeleteSpeci = () => {
    setSpeciToDelete(null)
    closeDeleteModal()
  }

  const confirmDeleteSpeci = () => {
    const queryKey = ['listSpecis', station?.recordNumber]
    const cacheBeforeDelete = queryClient.getQueryData(queryKey) as LoomQuery

    const resultsWithTargetSpeciRemoved = cacheBeforeDelete.listSpecis?.results?.filter(
      (speci: any) => speci.id !== speciToDelete?.id
    )
    const cacheAfterDelete = {
      ...cacheBeforeDelete,
      listSpecis: { results: resultsWithTargetSpeciRemoved },
    } as LoomQuery

    queryClient.setQueryData(queryKey, () => cacheAfterDelete)

    if (speciToDelete?.id) {
      deleteSpeciMutation.mutate(
        { speciId: speciToDelete.id },
        {
          onError: () => {
            queryClient.setQueryData(queryKey, () => cacheBeforeDelete)
          },
        }
      )
    }
    setSpeciToDelete(null)
    closeDeleteModal()
  }

  const startSpeci = () => {
    const newSpeciID = uuidv4()
    if (station) {
      navigateToNewSpeci(station?.recordNumber, newSpeciID)
    }
  }

  return (
    <>
      <Stack>
        <Group>
          <Title order={2} mr='auto'>
            Wool Specifications
          </Title>
          <Button onClick={startSpeci} leftSection={<IconFilePlus />}>
            Create wool specification
          </Button>
        </Group>
        <HubSpeciTable
          data={data?.listSpecis?.results ?? []}
          renderKey={JSON.stringify(queryKey)}
          isLoading={isLoading}
          isError={isError}
          deleteSpeci={askForDeleteConfirmation}
          reSendSpeci={openResendEmailModal}
        />
      </Stack>

      <Modal
        data-testid='delete-speci-modal'
        title={
          <Flex justify='flex-start' align='center'>
            <IconTrash size={28} color='var(--mantine-color-gray-4)' />
            <Text fw={500} size='lg' ml='sm'>
              Delete wool specification
            </Text>
          </Flex>
        }
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        centered
        closeButtonProps={{ 'aria-label': 'Close modal' }}
        returnFocus={false}
      >
        Are you sure you want to delete this specification?
        {speciToDelete && (
          <Text size='sm' c='dimmed' mt='xs'>
            Created by {speciToDelete.author} on{' '}
            {formatDate(new Date(speciToDelete.created), 'DD/MM/YYYY')}
          </Text>
        )}
        <Group justify='flex-end' mt='md' gap='xs'>
          <Button variant='transparent' onClick={cancelDeleteSpeci}>
            No
          </Button>
          <Button color='red' onClick={confirmDeleteSpeci}>
            Yes, delete
          </Button>
        </Group>
      </Modal>

      <EmailSpeciModal
        speciId={speciToResend?.id || ''}
        isModalOpen={isResendModalOpen}
        closeModal={closeResendModal}
        brand={station?.brand || ''}
      />
    </>
  )
}
